import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { Filter, FilterValue, FiltersConfig } from '../../_models/filter';
import { FormControl, Validators, FormBuilder, FormGroup, FormArray, AbstractControl } from '@angular/forms';
import { FilterService } from 'src/app/_services/filter.service';
import { FormErrorsService, atLeastOneCheckboxCheckedValidator } from 'src/app/_services/form-errors.service';
import moment from 'moment';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../_services/auth.service';
import { Location } from 'src/app/_models/user';
import { Subscription } from 'rxjs';
import { BarcodeFormat } from '@zxing/library';
import { AppService } from 'src/app/_services/app.service';

@Component({
  standalone: false,
  selector: 'app-filters-form',
  templateUrl: './filters-form.component.html',
  styleUrls: ['./filters-form.component.scss']
})
export class FiltersFormComponent implements OnInit, OnDestroy {
  @Input() filters: Filter[] = null;
  @Input() screenFilters: any = null;
  @Input() filtersConfig: FiltersConfig = null;
  @Output() submitFilters = new EventEmitter<any>();
  @Output() onChangeFilters = new EventEmitter<any>();
  //QR SCANNER
  currentIndex: number = 1;
  desiredDevice: Object = null;
  scanning: boolean = false;
  scannedValue: string;
  availableDevices: Array<Object> = [];
  allowedFormats = [BarcodeFormat.QR_CODE, BarcodeFormat.EAN_13, BarcodeFormat.CODE_128, BarcodeFormat.DATA_MATRIX];

  filtersForm: FormGroup;
  currentUserReq$: Subscription = null;
  loadingFilters: any = {};
  rangeFilter = {
    from_date: null,
    to_date: null,
    signed: '1',
  };
  currentLocation: Location = null;
  complexFiltersPage = {};
  docStatus: Array<Object> = [];

  public formErrorsFiltersData = {};

  constructor(
    private fb: FormBuilder,
    private filterService: FilterService,
    private formErrorService: FormErrorsService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private userService: AuthService,
    private appService: AppService) { }

  ngOnDestroy(): void {
    if (this.currentUserReq$) {
      this.currentUserReq$.unsubscribe();
    }
  }

  ngOnInit(): void {
    if (!this.filtersForm) {
      this.initForm();
    }
    this.loadFilters();
    this.loadConfig();
    this.getDocStatus();

    this.filtersForm.valueChanges.subscribe((data) => {
      this.checkValidatorFilterData();
    });

    this.currentUserReq$ = this.userService.currentUser$.subscribe(data => {
      if (data && data.selectedLocationIndex != null && data.selectedLocationIndex != undefined && data.selectedLocationIndex.toString() && data.locationRoles) {
        this.currentLocation = data.locationRoles[data.selectedLocationIndex];
      }
    });

    this.filtersForm.patchValue({ scan: this.scannedValue });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters && changes.filters.currentValue) {
      if (!this.filtersForm) {
        this.initForm();
      }
      this.loadFilters();
      this.loadConfig();
    }
  }

  public changeCamera() {
    if (this.availableDevices.length > 1) {
      this.currentIndex = this.currentIndex == 0 ? 1 : 0;
      this.desiredDevice = this.availableDevices[this.currentIndex];
    }
  }

  camerasFoundHandler(devices) {
    this.desiredDevice = devices[0];
    this.availableDevices = devices;
  }

  public beginScan() {
    this.scanning = true;
  }

  public scanSuccessHandler(data) {
    this.scannedValue = data;
    this.filtersForm.patchValue({ scan: this.scannedValue });
    this.scanning = false;
    this.submit();
  }

  checkValidatorFilterData() {
    this.formErrorsFiltersData = this.formErrorService.validateForm(this.filtersForm, this.formErrorsFiltersData, true);
  }

  initForm() {
    this.filtersForm = this.fb.group({
      from_date: [{ value: this.defaultDateFormatted(this.screenFilters?.dateRange?.from), disabled: false }, Validators.required],
      to_date: [{ value: this.defaultDateFormatted(this.screenFilters?.dateRange?.to), disabled: false }, Validators.required],
      signed: [{ value: '1', disabled: false }, Validators.required],
      scan: [{ value: '', disabled: false }]
    });
  }

  defaultDateFormatted(rangeDays: number = 0) {
    const today = moment();

    if (rangeDays < 0) {
      today.subtract(rangeDays * -1, 'days');
    }
    else if (rangeDays > 0) {
      today.add(rangeDays, 'days');
    }

    return { year: +today.format('YYYY'), month: +today.format('MM'), day: +today.format('DD') };
  }

  defaultForm() {
    this.filtersForm.patchValue({
      from_date: this.defaultDateFormatted(this.screenFilters.dateRange.from),
      to_date: this.defaultDateFormatted(this.screenFilters.dateRange.to),
      signed: '1'
    })
  }

  loadFilters() {
    if (!this.filters || !this.filters.length) {
      return;
    }

    if (!this.filtersForm) {
      this.initForm();
    }

    this.filters.forEach(f => {
      f['toggle'] = false;
      this.formErrorsFiltersData[f.name] = "";

      if (this.filtersForm.contains(f.name)) {
        return;
      }

      if (f.renderizationType !== 'radio') {
        this.filtersForm.addControl(
          f.name, new FormControl(null, f.required ? Validators.required : null)
        );
      }
      else {
        const config = this.filtersConfig && this.filtersConfig[f.name] ? this.filtersConfig[f.name] : [];
        this.complexFiltersPage[f.name] = 1;
        this.filtersForm.addControl(f.name, this.fb.array(config, f.required ? atLeastOneCheckboxCheckedValidator() : null));
      }
    });
  }

  getFilterControl(name: string): AbstractControl | null {
    return this.filtersForm?.get(name) || null;
  }

  loadConfig() {
    if (this.filtersConfig) {
      this.filtersForm.patchValue(this.filtersConfig);
      this.scannedValue = this.filtersConfig['scan'];
    }
  }

  submit() {
    this.submitFilters.emit(this.filtersForm.value);
  }

  toggleFilterCard(filter) {
    filter.toggle = !filter.toggle;
    if (!filter.toggle) return;
    this.getFilterData(filter);
  }

  getFilterData(filter) {
    this.rangeFilter = {
      from_date: this.parseNgbDate(this.filtersForm.get('from_date').value),
      to_date: this.parseNgbDate(this.filtersForm.get('to_date').value),
      signed: this.filtersForm.get('signed').value
    };
    this.loadingFilters[filter.name] = true;
    this.complexFiltersPage[filter.name] = 1;

    this.filterService.getFilterData(filter.apiSearch, this.rangeFilter, this.currentLocation).subscribe((data: FilterValue[]) => {
      if (data.length > 0) {
        this.loadingFilters[filter.name] = false;
        this.filters = this.filters.map(f => f.name === filter.name ? { ...f, values: data } : f);

        const filterControl = this.filtersForm.controls[filter.name] as FormArray;

        filterControl.value.forEach((el, i) => {
          if (!data.find(d => d.description === el)) filterControl.removeAt(i);
        });
      }
      else {
        filter.toggle = false;
        this.toastr.info(this.translateService.instant('filters.empty_values_description'), this.translateService.instant('filters.empty_values'));
      }
    }, err => {
      this.loadingFilters[filter.name] = false;
      this.toastr.info(this.translateService.instant('filters.empty_values_description'), this.translateService.instant('filters.empty_values'));
    });
  }

  public checkInArray = (filter, value) => {
    return (this.filtersForm.controls[filter] as FormArray).value.find(f => f === value);
  }

  clearQr() {
    this.scannedValue = "";
    let filters = JSON.parse(JSON.stringify(this.filters));
    filters.scan = this.scannedValue;
    this.filterService.storeFiltersConfig(filters);
    this.filtersForm.patchValue({ scan: this.scannedValue });
  }

  clear() {
    this.filtersForm.reset();
    this.clearQr();
    this.clearFilterValues();
    this.defaultForm();
    this.resetComplexFilters(true);
  }

  clearFilterValues() {
    this.filters.forEach(f => { f.values ? f.values = null : f });
  }

  onChangeRange() {
    this.resetComplexFilters();
  }

  onSelectFilterValue(d, filter) {
    const filterControl = this.filtersForm.controls[filter.name] as FormArray;
    const index = filterControl.value.findIndex(el => el === d.description);
    if (index === -1) filterControl.push(new FormControl(d.description));
    else filterControl.removeAt(index);
  }

  resetComplexFilters(collapse: boolean = false) {
    let controls = {};
    this.filters.forEach(f => {
      if (f.renderizationType === 'radio' && this.filtersForm.controls[f.name].value.length > 0) {
        (this.filtersForm.controls[f.name] as FormArray).clear();
      }
      if (collapse) f['toggle'] = false;

      if (f['toggle']) this.getFilterData(f);
    });
    this.filtersForm.patchValue(controls);
  }

  parseNgbDate(ngbDate: NgbDate) {
    return moment(new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day)).format();
  }

  removeFilterSelection(el, filter) {
    this.onSelectFilterValue({ description: el }, { name: filter });
  }

  getDocStatus(){
    this.docStatus = JSON.parse(localStorage.getItem('docStatus'));
  }

  hasFilter(type){
    if (!this.filters || !this.filters.length) {
      return false;
    }

    if(this.filters.find(x => x.renderizationType.toUpperCase() === type.toUpperCase()) != undefined){
      return true;
    }
    return false;
  }
}
